import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { observer } from 'mobx-react'
import { PositionApplicationStore } from 'stores/position-application'
import { Column, Row } from 'components/common/layout'
import {
  PositionApplicationSubBtn,
  PositionApplicationMainBtn,
  PositionApplicationFormContainer,
  PositionApplicationSubmitBtn,
  PositionApplicationStepSubText,
  PositionApplicationFormContentContainer,
  PositionApplicationFormScrollContainer,
} from 'containers/positions/application/styles'
import { PositionApplicationFulfillment } from 'containers/positions/application/fulfillment-form'
import { PositionApplicationFitReason } from 'containers/positions/application/fit-reason-form'
import { getPositionApplicationPreviewSkipStorage } from 'utils/storages'
import {
  hasFitApplicationFulfillment,
  hasFitApplicationFulfillmentDescription,
} from 'utils/validations'
import { useStores } from 'stores/globals'
import { BtnSize, BtnType, Button } from 'components/common/button'
import { BaseText, SemiBoldText } from 'components/common/text'
import { theme } from 'infra/theme'
import { calcDateBefore } from 'utils/displays'
import { useScoutMyRequest, useTempApplication } from 'apis/hooks'
import { PositionApplicationStepTitleText } from 'containers/positions/application/styles'
import {
  calculatePositionFulfillmentScore,
  getProfileCompleteStatus,
} from 'utils/formatters'
import { detectAndNoticePrivateInfoFromApplication } from 'utils/handlers'
import { FiChevronRight } from 'react-icons/fi'

export const PositionApplicationFitReasonStep: React.FC<{
  store: PositionApplicationStore
}> = observer(({ store }) => {
  const { positionListStore } = useStores()
  const { data: tempApplication } = useTempApplication(
    store.positionDetail?.id || 0,
  )
  const { data: profile } = useScoutMyRequest()
  const [isToastVisible, setIsToastVisible] = useState(false)
  const [isToastBouncing, setIsToastBouncing] = useState(false)
  const { isAbleToApply } = getProfileCompleteStatus(profile)
  const isSkipPreview = getPositionApplicationPreviewSkipStorage()
  const hasUnwrittenItems =
    !store.joinReason?.trim() ||
    !hasFitApplicationFulfillment(store.fulfillments) ||
    !hasFitApplicationFulfillmentDescription(store.fulfillments)
  const score = calculatePositionFulfillmentScore(store.fulfillments)
  const isActive = score >= 50

  const scoreColor = isActive
    ? theme.color.primary
    : score > 0
    ? theme.color.gray2
    : theme.color.alert

  const detectPrivateInfoAndUpdateTemp = async (): Promise<boolean> => {
    // '적합하다 생각하는 이유' 단계에서 작성한 것이 있어야 함
    if (
      !store.fitReason.trim() &&
      !hasFitApplicationFulfillmentDescription(store.fulfillments)
    )
      return false
    const fulfillmentsWithDesc = Object.values(store.fulfillments)
      .flat()
      .filter((item) => !!item.description)
    // fitReason, fulfillment 중 하나만이라도 isSaving이면 되어서, 임시저장중 표시에 영향 미치지 않는 isSavingFulfillment 사용
    store.setState('isSavingFulfillment', true)
    const hasDetected = await detectAndNoticePrivateInfoFromApplication(
      store.positionDetail?.id || 0,
      { fitReason: store.fitReason.trim(), fulfillments: fulfillmentsWithDesc },
    )
    // 이 함수를 try catch안에서 호출할 경우, 위 detectAndNoticePrivateInfoFromApplication에서 에러 나면 아래 코드 라인이 실행되지 않을 수 있으니 유의!! finally 처리 필요
    store.setState('isSavingFulfillment', false)
    // 2-1. 탐지된 게 없으면 다음으로 넘어감
    if (!hasDetected) return false
    // 2-2. 탐지된 게 있으면 mutate한 데이터로 업데이트
    store.mutateAndUpdateTemp()
    return true
  }

  const validateApplication = () => {
    if (isToastVisible) {
      setIsToastBouncing(true)
      return false
    }
    if (!isAbleToApply && !store.hasAttachment) {
      setIsToastVisible(true)
      return false
    }
    return true
  }

  const apply = async () => {
    if (store.isSavingFitReason || store.isSavingFulfillment) return
    if (!validateApplication()) return
    try {
      const hasDetected = await detectPrivateInfoAndUpdateTemp()
      if (hasDetected) return
      if (hasUnwrittenItems)
        return store.setState('isOpenMessageCheckModal', true)
      if (!confirm(store.applicationAlert)) return
      await store.apply(!!store.positionDetail?.userApplication.isPossibleApply)
      positionListStore.mutatePositionList()
      store.setState('isOpenDoneModal', true)
    } catch (e) {
      alert(e)
    } finally {
      store.setState('isSavingFulfillment', false)
    }
  }

  const updateFulfillments = async () => {
    if (!store.positionDetail?.id) return
    if (
      !confirm(
        '기존에 작성했던 채용 공고 적합도 내용이 사라져요.\n그래도 진행하시겠어요?',
      )
    )
      return
    await store.updateFulfillmentsAndSetTemp(store.positionDetail.id)
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => store.setState('curTab', 0), [])

  return (
    <>
      {tempApplication?.hasNewConditions && (
        <ToastContainer>
          <CustomToast>
            <Column style={{ gap: 4 }}>
              <BaseText style={{ fontSize: 14, color: theme.color.black2 }}>
                {calcDateBefore(store.positionDetail?.updatedAt || '')}에 채용
                공고가 수정되었어요
              </BaseText>
              <SemiBoldText>변경된 채용 공고로 작성하시겠어요?</SemiBoldText>
            </Column>
            <UpdateBtn
              size={BtnSize.SMALL}
              variant={BtnType.PRIMARY2}
              onClick={updateFulfillments}
            >
              변경
            </UpdateBtn>
          </CustomToast>
        </ToastContainer>
      )}
      <PositionApplicationFormContainer>
        <PositionApplicationFormContentContainer>
          <TitleWrapper>
            <PositionApplicationStepTitleText>
              채용 조건에 얼마나 적합한지 알려주세요
            </PositionApplicationStepTitleText>
            <PositionApplicationStepSubText>
              스타트업의 조건에 부합하는 본인의 강점을 어필해 보세요
            </PositionApplicationStepSubText>
          </TitleWrapper>
          <PositionApplicationFormScrollContainer style={{ gap: 40 }}>
            <Column style={{ gap: 40, padding: 10 }}>
              <PositionApplicationFulfillment store={store} />
            </Column>
            <PositionApplicationFitReason store={store} />
          </PositionApplicationFormScrollContainer>
        </PositionApplicationFormContentContainer>
        <Column style={{ position: 'relative', gap: 8, textAlign: 'center' }}>
          {isToastVisible && (
            <Toast
              isBouncing={isToastBouncing}
              onClick={() => store.setState('curStep', 0)}
            >
              <SemiBoldText style={{ color: theme.color.white1 }}>
                내 정보(필수)를 추가하지 않았어요!
              </SemiBoldText>
              <Row>
                <SemiBoldText style={{ color: theme.color.primary }}>
                  추가하기
                </SemiBoldText>
                <FiChevronRight size={20} color={theme.color.primary} />
              </Row>
            </Toast>
          )}
          <ScoreText>
            채용 조건을 <b style={{ color: scoreColor }}>{score}%</b> 만족해요
          </ScoreText>
          <Row style={{ gap: 8 }}>
            <PositionApplicationSubBtn onClick={() => store.movePrevStep()}>
              이전
            </PositionApplicationSubBtn>
            {isSkipPreview ? (
              <PositionApplicationSubmitBtn onClick={apply}>
                지원하기
              </PositionApplicationSubmitBtn>
            ) : (
              <PositionApplicationMainBtn
                onClick={async () => {
                  if (store.isSavingFitReason || store.isSavingFulfillment)
                    return
                  if (!validateApplication()) return
                  try {
                    const hasDetected = await detectPrivateInfoAndUpdateTemp()
                    if (hasDetected) return
                    if (hasUnwrittenItems)
                      return store.setState('isOpenMessageCheckModal', true)
                    store.setState('isOpenPreviewModal', true)
                  } catch (e) {
                    alert(e)
                  } finally {
                    store.setState('isSavingFulfillment', false)
                  }
                }}
              >
                다음
              </PositionApplicationMainBtn>
            )}
          </Row>
        </Column>
      </PositionApplicationFormContainer>
    </>
  )
})

const ToastContainer = styled(Row)`
  top: 60px;
  left: 0px;
  width: 100%;
  position: fixed;
  justify-content: center;
`
const CustomToast = styled(Row)`
  gap: 40px;
  padding: 8px 16px;
  border-radius: 8px;
  background-color: #ffeaee;
  box-shadow: 0px 4px 4px 0px rgba(0, 0, 0, 0.08);
`

const UpdateBtn = styled(Button)`
  width: fit-content;
  box-shadow: 0px 4px 8px 0px rgba(0, 0, 0, 0.08);
`

const ScoreText = styled(SemiBoldText)`
  font-size: ${(p) => p.theme.fontSize.desc};
`

const TitleWrapper = styled(Column)`
  gap: 4px;
`

const Toast = styled(Row)<{ isBouncing: boolean }>`
  position: absolute;
  top: -16px;
  left: 0;
  width: 100%;
  padding: 8px 8px 8px 12px;
  gap: 8px;
  justify-content: space-between;
  background-color: rgba(0, 0, 0, 0.65);
  border-radius: 4px;
  cursor: pointer;
  ${(p) =>
    p.isBouncing &&
    `
    animation: NotAbleToApplyBounce 0.8s ease-out infinite;
  `}

  @keyframes NotAbleToApplyBounce {
    0% {
      transform: translateY(0);
    }
    30% {
      transform: translateY(-8px);
    }
    60% {
      transform: translateY(4px);
    }
    100% {
      transform: translateY(0);
    }
  }
`
